import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Chip, Tooltip } from '@mui/material';
import { Close } from '@mui/icons-material';
import { setSearch } from 'store/features/globalSearch';
import { useGlobalSearch } from 'hooks';
import { FilterGroupProps } from '../FilterGroup/FilterGroup';
import { getUserFriendlyLabel } from '../filter-helpers';
import styles from './FilterChips.module.scss';

export interface FilterChipsProps {
    /** All of the FilterGroups present in the current FilterBar. */
    filterGroups: FilterGroupProps[];
}

/**
 * FilterChips displays deletable chips for the active filters on the FilterBar.
 * The FilterChips component is include with the FilterBar component and is displayed below the filter dropdowns by default.
 * If you'd prefer to place the FilterChips in a custom location, this component can be added
 * separately so long as the filterGroups passed into the FilterBar and FilterChips components contain the same data.
 */
export const FilterChips = ({ filterGroups }: FilterChipsProps) => {
    const [showClearAll, setShowClearAll] = useState<boolean>(false);
    const { globalSearchTerm } = useGlobalSearch();
    const dispatch = useDispatch();

    const handleDelete = (
        filterGroup: FilterGroupProps,
        deletedFilterName: string
    ) => {
        const newFilters: Record<string, boolean> = {};
        Object.keys(filterGroup.filters).forEach((filterName: string) => {
            // Set deleted filter to false.
            if (filterName === deletedFilterName)
                newFilters[deletedFilterName] = false;
            // Otherwise keep the current value.
            else newFilters[filterName] = filterGroup.filters[filterName];
        });
        filterGroup.onUpdateFilters(newFilters);
    };

    const clearSearch = (): void => {
        dispatch(setSearch(''));
    };

    const clearAll = (): void => {
        clearSearch();
        filterGroups.forEach((filterGroup: FilterGroupProps) => {
            const newFilters: Record<string, boolean> = {};
            Object.keys(filterGroup.filters).forEach((filterName: string) => {
                newFilters[filterName] = false;
            });
            filterGroup.onUpdateFilters(newFilters);
        });
    };

    // If all filters in the filter group are on (i.e., set to `true`), do not show chips for that filter group.
    const allFiltersInGroupAreOn = (filterGroup: FilterGroupProps): boolean => {
        const filterValues = Object.values(filterGroup.filters);
        return (
            filterValues.filter((filterValue: boolean) => filterValue === true)
                .length === filterValues.length
        );
    };

    const allFiltersInGroupAreOff = (
        filterGroup: FilterGroupProps
    ): boolean => {
        const filterValues = Object.values(filterGroup.filters);
        return (
            filterValues.filter((filterValue: boolean) => filterValue === false)
                .length === filterValues.length
        );
    };

    const shouldShowClearAll = (filterGroups: FilterGroupProps[]): boolean => {
        let shouldShowClearAll = false;

        for (const group of filterGroups) {
            if (
                !allFiltersInGroupAreOn(group) &&
                !allFiltersInGroupAreOff(group)
            )
                shouldShowClearAll = true;
        }

        return shouldShowClearAll;
    };

    useEffect(() => {
        if (filterGroups) {
            setShowClearAll(shouldShowClearAll(filterGroups));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [[filterGroups]]);

    return (
        <div data-testid="FilterChips" className={styles.FilterChips}>
            {showClearAll ? (
                <Tooltip title={'Clear All Filters'}>
                    <Button startIcon={<Close />} onClick={() => clearAll()}>
                        Clear All{' '}
                    </Button>
                </Tooltip>
            ) : null}

            {globalSearchTerm ? (
                <Tooltip
                    title={`Search Term: ${globalSearchTerm}`}
                    sx={{ marginRight: '5px' }}
                >
                    <Chip
                        label={globalSearchTerm}
                        onDelete={() => clearSearch()}
                    />
                </Tooltip>
            ) : null}

            {filterGroups.map((filterGroup: FilterGroupProps) => {
                if (allFiltersInGroupAreOn(filterGroup)) return null;

                // Otherwise, show chips that are on (i.e., set to `true`).
                return Object.keys(filterGroup.filters).map(
                    (filterName: string) => {
                        if (filterGroup.filters[filterName])
                            return (
                                <Tooltip
                                    sx={{ marginRight: '5px' }}
                                    title={`${
                                        filterGroup.name
                                    }: ${getUserFriendlyLabel(
                                        filterGroup.name,
                                        filterName
                                    )}`}
                                    key={filterName}
                                >
                                    <Chip
                                        data-testid={'filter-chip'}
                                        key={filterName}
                                        label={getUserFriendlyLabel(
                                            filterGroup.name,
                                            filterName
                                        )}
                                        onDelete={() =>
                                            handleDelete(
                                                filterGroup,
                                                filterName
                                            )
                                        }
                                    />
                                </Tooltip>
                            );
                        else return null;
                    }
                );
            })}
        </div>
    );
};
